import { Button } from '@qr-front/ui/components/Button'
import {
	isQrPasswordValid,
	normalizeQrPassword,
	QrPasswordFields,
} from 'features/shared/client'
import { useAtom, useAtomValue } from 'jotai'
import dynamic from 'next/dynamic'
import { useTranslations } from 'next-intl'
import { useState } from 'react'

import { showNotification } from '@/components/Notification'
import { qrCodesApi } from '@/generator/shared'

import { enableQrPasswordDialogAtom, qrItemState } from '../dialogsState'
import { patchQrPasswordInList } from '../qrPasswordListState'
import { useDisablePasswordRequest } from './useDisablePasswordRequest'

const Dialog = dynamic(
	() => import('@qr-front/ui/components/Dialog').then((mod) => mod.Dialog),
	{ ssr: false },
)
const DialogContent = dynamic(
	() =>
		import('@qr-front/ui/components/Dialog').then(
			(mod) => mod.DialogContent,
		),
	{ ssr: false },
)

type TEnablePasswordFormProps = {
	qrId: string
	onEnabled: () => void
}

const EnablePasswordForm = ({ qrId, onEnabled }: TEnablePasswordFormProps) => {
	const t = useTranslations()
	const [password, setPassword] = useState('')
	const [repeat, setRepeat] = useState('')
	const [submitAttempted, setSubmitAttempted] = useState(false)
	const [isSaving, setIsSaving] = useState(false)

	const handleEnable = async () => {
		if (isSaving) return
		if (!isQrPasswordValid(password, repeat)) {
			setSubmitAttempted(true)
			return
		}
		setIsSaving(true)
		try {
			await qrCodesApi.putQrPassword(qrId, normalizeQrPassword(password))
			patchQrPasswordInList(qrId, true)
			onEnabled()
			showNotification(t('dashboard__qr_password__toast__enabled'), {
				type: 'success',
			})
		} catch (error) {
			console.error('Enabling QR password failed', error)
			showNotification(t('public__generic__unexpected_error'), {
				type: 'error',
			})
		} finally {
			setIsSaving(false)
		}
	}

	return (
		<>
			<QrPasswordFields
				password={password}
				repeat={repeat}
				onPasswordChange={setPassword}
				onRepeatChange={setRepeat}
				submitAttempted={submitAttempted}
			/>
			<Button
				type="button"
				disabled={isSaving}
				onClick={handleEnable}
				className="w-full font-normal disabled:opacity-50"
			>
				{t('dashboard__qr_password__popup__enable_button')}
			</Button>
		</>
	)
}

const EnablePasswordDialog = () => {
	const t = useTranslations()
	const [open, setOpen] = useAtom(enableQrPasswordDialogAtom)
	const qrItem = useAtomValue(qrItemState)

	if (!qrItem) return null

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogContent
				closeButtonLabel={t('ui__popup__close_button')}
				className="p-6 tablet:p-8"
				onOpenAutoFocus={(event) => event.preventDefault()}
			>
				<div className="flex flex-col gap-6">
					<div className="flex flex-col gap-2 text-center">
						<h3 className="font-bold type-heading-3">
							{t('dashboard__qr_password__popup__title')}
						</h3>
						<p className="type-body-medium text-dark-grey">
							{t('dashboard__qr_password__popup__text')}
						</p>
					</div>
					<EnablePasswordForm
						qrId={qrItem.id}
						onEnabled={() => setOpen(false)}
					/>
				</div>
			</DialogContent>
		</Dialog>
	)
}

export const QrPasswordDialogs = () => {
	useDisablePasswordRequest()

	return <EnablePasswordDialog />
}
